
import Table from "../../../components/Table";
import MultiSelectInput from "../../../components/Select/MultiSelectInput";
import { useAuth } from "../../../context/AuthContext";
import { getPackages } from "../../../services/packagesService";
import { deleteSubscription, getRenewStats, getSubscriptions } from "../../../services/renewService";
import { showErrorToast, showSuccessToast } from "../../../services/toastService";
import { Link, useSearchParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { hasPermission } from "../../../utils/permissions";

type SubscriptionRow = {
    _id: string;
    member?: {
        firstName?: string;
        lastName?: string;
    };
    package?: {
        _id?: string;
        id?: string;
        name?: string;
    };
    startDate?: string;
    expirationDate?: string;
    status?: boolean;
    notify?: boolean;
    price?: number;
    daysLeft?: number;
    renewalState?: string;
    renewalLabel?: string;
};

const readRows = (response: unknown): SubscriptionRow[] => {
    if (Array.isArray(response)) return response as SubscriptionRow[];
    if (!response || typeof response !== "object") return [];

    const payload = response as Record<string, unknown>;
    if (Array.isArray(payload.data)) return payload.data as SubscriptionRow[];
    if (Array.isArray(payload.subscriptions)) return payload.subscriptions as SubscriptionRow[];
    if (Array.isArray(payload.items)) return payload.items as SubscriptionRow[];

    return [];
};

const readRenewalRows = (response: unknown): SubscriptionRow[] => {
    if (!response || typeof response !== "object") return [];

    const payload = response as Record<string, unknown>;
    const rows = Array.isArray(payload.renewalsWindow) ? payload.renewalsWindow : [];

    return rows.map((item, index) => {
        const row = item && typeof item === "object" ? item as Record<string, any> : {};

        return {
            _id: String(row.subscriptionId ?? row._id ?? row.id ?? index + 1),
            member: row.member,
            package: row.package,
            startDate: row.startDate,
            expirationDate: row.expirationDate,
            status: row.status,
            notify: row.notify,
            price: row.price,
            daysLeft: typeof row.daysLeft === "number" ? row.daysLeft : Number(row.daysLeft ?? 0),
            renewalState: String(row.state ?? ""),
            renewalLabel: String(row.label ?? ""),
        };
    });
};

const formatDate = (value?: string) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toISOString().split("T")[0];
};

const formatMemberName = (row: SubscriptionRow) => {
    const firstName = row.member?.firstName ?? "";
    const lastName = row.member?.lastName ?? "";
    return [firstName, lastName].filter(Boolean).join(" ") || "-";
};

const AllSubscriptionsPage = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const isRenewalsDueView = searchParams.get("view") === "renewalsDue";
    const daysWindow = Math.max(Number(searchParams.get("days") || 5) || 5, 1);
    const canEditSubscriptions = hasPermission(user, "subscriptions.write");
    const [rows, setRows] = useState<SubscriptionRow[]>([]);
    const [packages, setPackages] = useState<any[]>([]);
    const [packageFilter, setPackageFilter] = useState<string[]>(searchParams.getAll("packageId"));
    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const packageOptions = packages.map((pkg) => ({
        value: pkg._id,
        label: pkg.name,
    }));
    const visibleRows = isRenewalsDueView && packageFilter.length
        ? rows.filter((row) => {
            const packageId = row.package?._id ?? row.package?.id;
            return packageId ? packageFilter.includes(packageId) : false;
        })
        : rows;

    const StatusCell = ({ value }: { value: boolean }) => (
        <span className={`px-2 py-1 rounded-md ${value ? "text-green-500" : "text-red-500"}`}>
            {value ? "Active" : "Inactive"}
        </span>
    );

    const NotifyCell = ({ value }: { value?: boolean }) => (
        <span className={`px-2 py-1 rounded-md ${value ? "text-green-600" : "text-gray-600"}`}>
            {value ? "Yes" : "No"}
        </span>
    );

    const RenewalStatusCell = ({ row }: { row: { original: SubscriptionRow } }) => {
        const item = row.original;
        const isExpired = item.renewalState === "expired" || (item.daysLeft ?? 0) < 0;

        return (
            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${isExpired ? "bg-rose-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                {item.renewalLabel || `${item.daysLeft ?? 0} days left`}
            </span>
        );
    };

    const baseColumns = [
        {
            Header: "Member",
            accessor: "member",
            Cell: ({ row }: { row: { original: SubscriptionRow } }) => formatMemberName(row.original),
        },
        {
            Header: "Package",
            accessor: "package",
            Cell: ({ row }: { row: { original: SubscriptionRow } }) => row.original.package?.name ?? "-",
        },
        { Header: "Start Date", accessor: "startDate", Cell: ({ value }: { value?: string }) => formatDate(value) },
        { Header: "End Date", accessor: "expirationDate", Cell: ({ value }: { value?: string }) => formatDate(value) },
    ];

    const columns = [
        ...baseColumns,
        ...(isRenewalsDueView
            ? [{ Header: "Renewal Status", accessor: "renewalLabel", Cell: RenewalStatusCell }]
            : [{ Header: "Status", accessor: "status", Cell: StatusCell }]),
        { Header: "Notify", accessor: "notify", Cell: NotifyCell },
        { Header: "Price", accessor: "price" },
        ...(canEditSubscriptions
            ? [{
                Header: "Actions",
                accessor: "actions",
                Cell: ({ row }: { row: { original: SubscriptionRow } }) => {
                    const subscriptionId = row?.original?._id;
                    const isDeleting = deletingId === subscriptionId;

                    return (
                        <button
                            type="button"
                            onClick={() => handleDelete(subscriptionId)}
                            disabled={!subscriptionId || isDeleting}
                            className="px-3 py-1 rounded-md bg-gray-800 hover:bg-black transition text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDeleting ? "Deleting..." : "Delete"}
                        </button>
                    );
                },
            }]
            : []),
    ];

    const handleDelete = async (subscriptionId: string) => {
        if (!subscriptionId) return;
        const confirmed = window.confirm("Delete this subscription?");
        if (!confirmed) return;

        try {
            setDeletingId(subscriptionId);
            await deleteSubscription(subscriptionId);
            setRows((prev) => prev.filter((row) => row._id !== subscriptionId));
            showSuccessToast("Subscription deleted");
        } catch (err) {
            console.error("Failed to delete subscription", err);
            showErrorToast("Failed to delete subscription");
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        const fetchRows = async () => {
            if (!user?.id) return;

            try {
                setLoading(true);
                if (isRenewalsDueView) {
                    const response = await getRenewStats(daysWindow);
                    setRows(readRenewalRows(response));
                } else {
                    const response = await getSubscriptions({
                        scope: "thisMonth",
                        page: 1,
                        limit: 1000,
                        type: "normal",
                        packageId: packageFilter,

                    });
                    setRows(readRows(response));
                }
            } catch (err) {
                console.error("Failed to fetch all subscriptions", err);
            } finally {
                setLoading(false);
            }
        };

        fetchRows();
    }, [user?.id, packageFilter, isRenewalsDueView, daysWindow]);

    useEffect(() => {
        const fetchPackages = async () => {
            if (!user?.id) return;

            try {
                const response = await getPackages();
                setPackages(Array.isArray(response) ? response : []);
            } catch (err) {
                console.error("Failed to fetch packages", err);
                showErrorToast("Failed to fetch packages");
            }
        };

        fetchPackages();
    }, [user?.id]);

    return (
        <div className="w-full">
            <div className="mb-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                <div>
                    <h1 className="text-2xl font-bold">{isRenewalsDueView ? "Renewals Due" : "This Month Subscriptions"}</h1>
                    <p className="text-sm text-gray-500">
                        {isRenewalsDueView
                            ? `Members expired recently or expiring in the next ${daysWindow} days.`
                            : "All subscriptions from the current month."}
                    </p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <MultiSelectInput
                        label="Packages"
                        name="packageFilter"
                        value={packageFilter}
                        options={packageOptions}
                        placeholder="All packages"
                        onChange={setPackageFilter}
                    />
                    <Link
                        to={isRenewalsDueView ? "/" : "/subscriptions"}
                        className="rounded-md border border-gray-300 px-4 py-2 text-center text-sm font-semibold hover:bg-gray-100"
                    >
                        {isRenewalsDueView ? "Back to Dashboard" : "Back"}
                    </Link>
                </div>
            </div>

            {loading && <p className="text-sm text-gray-500">{isRenewalsDueView ? "Loading renewals..." : "Loading subscriptions..."}</p>}

            {!loading && visibleRows.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                    {isRenewalsDueView ? "No renewals due in this window." : "No subscriptions found."}
                </div>
            ) : (
                <Table
                    columns={columns}
                    data={visibleRows}
                    pageSize={Math.max(visibleRows.length, 1)}
                    add={false}
                    renew={false}
                    checkbox={true}
                    showSearch={false}
                    showPagination={false}
                />
            )}
        </div>
    );
};

export default AllSubscriptionsPage;
